import React, { useMemo } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import baseTheme from '../../theme';
import { useTenant } from '../../contexts/TenantContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';

/**
 * Applies dark/light mode + tenant white-label branding onto the MUI theme.
 */
const BrandingThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { tenant, getBranding, getFeatures } = useTenant();
  const { isAuthenticated } = useAuth();
  const { mode, primaryColor } = useAppTheme();

  const theme = useMemo(() => {
    const features = (isAuthenticated && tenant ? getFeatures() : {}) as Record<string, boolean>;
    const plan = tenant?.plan;
    const whiteLabel =
      isAuthenticated &&
      tenant &&
      (features.whiteLabel === true || plan === 'enterprise' || plan === 'custom');

    const branding = whiteLabel ? getBranding() : null;
    const primary = branding?.primaryColor || primaryColor || '#1976d2';
    const secondary = branding?.secondaryColor || '#dc004e';

    return createTheme({
      ...baseTheme,
      palette: {
        ...baseTheme.palette,
        mode,
        primary: { main: primary },
        secondary: { main: secondary },
        ...(mode === 'dark'
          ? {
              background: {
                default: '#121212',
                paper: '#1e1e1e',
              },
            }
          : {
              background: {
                default: '#f5f5f5',
                paper: '#ffffff',
              },
            }),
      },
    });
  }, [isAuthenticated, tenant, getBranding, getFeatures, mode, primaryColor]);

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

export default BrandingThemeProvider;
